import { useStoreActions, useStoreState } from '@/stores/store';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import { getFullExerciseName } from '@/utils/exerciseNames';
import utils from '@/utils/utils';
import SpeedDropdown from '@/components/SpeedDropdown';
import colors from '@/config/colors';
import { Divider } from 'react-native-elements';
import { getPlaybackRate } from '@/utils/getPlaybackRate';
import SelectButton from '@/components/ui/therapist/SelectButton/SelectButton';

const styles = StyleSheet.create({
  subheading: { fontSize: 20, fontWeight: '600', marginBottom: 12 },
  selectLabel: { fontSize: 16, fontWeight: '400', marginBottom: 6 },
  selectContainer: { flexDirection: 'row', gap: 32, alignItems: 'flex-end' },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: colors.newUI.therapist.primaryBlue10,
    marginVertical: 8,
  },
});

function AuditoryLanguageComprehension() {
  const updateSettings = useStoreActions((actions) => actions.updateSettings);

  const copyWordsetExerciseTypes = useStoreState(
    (state) => state.copyWordsetExerciseTypes,
  );
  const copyTextsetExerciseTypes = useStoreState(
    (state) => state.copyTextsetExerciseTypes,
    (prev, next) => {
      if (
        prev[0]?.setting !== next[0]?.setting ||
        prev[0]?.value !== next[0]?.value ||
        prev[0]?.playbackRate !== next[0]?.playbackRate
      ) {
        return false;
      } else {
        return true;
      }
    },
  );
  const copySentencesetExerciseTypes = useStoreState(
    (state) => state.copySentencesetExerciseTypes,
    (prev, next) => {
      if (
        prev[0]?.setting !== next[0]?.setting ||
        prev[0]?.value !== next[0]?.value ||
        prev[0]?.playbackRate !== next[0]?.playbackRate
      ) {
        return false;
      } else {
        return true;
      }
    },
  );

  return (
    <View>
      <View style={{ padding: 20 }}>
        <Text style={styles.subheading}>Wörter</Text>
        <View style={styles.selectContainer}>
          <View>
            <Text style={styles.selectLabel}>Art der Ablenker</Text>
            <SelectButton
              options={[
                { label: 'Unrelatiert', value: 'unrelated' },
                { label: 'Semantisch', value: 'related' },
                { label: 'Aus Set', value: 'fromset' },
              ]}
              value={
                copyWordsetExerciseTypes.find(
                  ({ name }) =>
                    name === getFullExerciseName('Understanding', 'wordset'),
                )?.setting
              }
              onValueChange={(newValue) =>
                updateSettings({
                  newValue,
                  setType: 'wordset',
                  exerciseName: getFullExerciseName('Understanding', 'wordset'),
                  fieldToBeUpdated: 'setting',
                })
              }
            />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.selectLabel}>Anzahl Bilder</Text>
            <SelectButton
              options={[
                { label: '2', value: 2 },
                { label: '3', value: 3 },
                { label: '4', value: 4 },
              ]}
              value={
                copyWordsetExerciseTypes.find(
                  ({ name }) =>
                    name === getFullExerciseName('Understanding', 'wordset'),
                )?.value
              }
              onValueChange={(newValue) =>
                updateSettings({
                  newValue,
                  setType: 'wordset',
                  exerciseName: getFullExerciseName('Understanding', 'wordset'),
                  fieldToBeUpdated: 'value',
                })
              }
            />
          </View>
        </View>
        <SpeedDropdown
          updateSettings={updateSettings}
          value={String(
            getPlaybackRate(
              copyWordsetExerciseTypes,
              'Understanding',
              'wordset',
            ),
          )}
          exerciseName="Understanding"
          setType="wordset"
        />
      </View>
      <Divider width={1} color={colors.newUI.therapist.primaryBlue10} />
      <View style={{ padding: 20 }}>
        <Text style={styles.subheading}>Sätze</Text>
        <View style={styles.selectContainer}>
          <View>
            <Text style={styles.selectLabel}>Art der Ablenker</Text>
            <SelectButton
              options={[
                { label: 'Unrelatiert', value: 'unrelated' },
                { label: 'Semantisch', value: 'related' },
                { label: 'Aus Set', value: 'fromset' },
              ]}
              value={
                copySentencesetExerciseTypes.find(
                  ({ name }) =>
                    name ===
                    getFullExerciseName('Understanding', 'sentenceset'),
                )?.setting
              }
              onValueChange={(newValue) =>
                updateSettings({
                  newValue,
                  setType: 'sentenceset',
                  exerciseName: getFullExerciseName(
                    'Understanding',
                    'sentenceset',
                  ),
                  fieldToBeUpdated: 'setting',
                })
              }
            />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.selectLabel}>Anzahl Bilder</Text>
            <SelectButton
              options={[
                { label: '2', value: 2 },
                { label: '3', value: 3 },
                { label: '4', value: 4 },
              ]}
              value={
                copySentencesetExerciseTypes.find(
                  ({ name }) =>
                    name ===
                    getFullExerciseName('Understanding', 'sentenceset'),
                )?.value
              }
              onValueChange={(newValue) =>
                updateSettings({
                  newValue,
                  setType: 'sentenceset',
                  exerciseName: getFullExerciseName(
                    'Understanding',
                    'sentenceset',
                  ),
                  fieldToBeUpdated: 'value',
                })
              }
            />
          </View>
        </View>
        <SpeedDropdown
          updateSettings={updateSettings}
          value={String(
            getPlaybackRate(
              copySentencesetExerciseTypes,
              'Understanding',
              'sentenceset',
            ),
          )}
          exerciseName="Understanding"
          setType="sentenceset"
        />
      </View>
      <Divider width={1} color={colors.newUI.therapist.primaryBlue10} />
      <View style={{ padding: 20 }}>
        <Text style={styles.subheading}>Texte</Text>
        <View style={styles.selectContainer}>
          <View>
            <Text style={styles.selectLabel}>Art der Fragen</Text>
            <SelectButton
              options={[
                { label: 'Offen', value: 'open' },
                { label: 'Geschlossen', value: 'closed' },
              ]}
              value={
                copyTextsetExerciseTypes.find(
                  ({ name }) =>
                    name === getFullExerciseName('Understanding', 'textset'),
                )?.setting
              }
              onValueChange={(newValue) =>
                updateSettings({
                  newValue,
                  setType: 'textset',
                  exerciseName: getFullExerciseName('Understanding', 'textset'),
                  fieldToBeUpdated: 'setting',
                })
              }
            />
          </View>

          <View
            style={
              utils.determineSettings(
                copyTextsetExerciseTypes,
                getFullExerciseName('Understanding', 'textset'),
                'text',
                'setting',
                'closed',
              )
                ? {}
                : { opacity: 0.3, pointerEvents: 'none' }
            }
          >
            <Text style={styles.selectLabel}>
              Anzahl der Antwortmöglichkeiten
            </Text>
            <SelectButton
              options={[
                { label: '2', value: 2 },
                { label: '3', value: 3 },
                { label: '4', value: 4 },
              ]}
              value={
                copyTextsetExerciseTypes.find(
                  ({ name }) =>
                    name === getFullExerciseName('Understanding', 'textset'),
                )?.value
              }
              onValueChange={(newValue) =>
                updateSettings({
                  newValue,
                  setType: 'textset',
                  exerciseName: getFullExerciseName('Understanding', 'textset'),
                  fieldToBeUpdated: 'value',
                })
              }
            />
          </View>
        </View>
        <SpeedDropdown
          updateSettings={updateSettings}
          value={String(
            getPlaybackRate(
              copyTextsetExerciseTypes,
              'Understanding',
              'textset',
            ),
          )}
          exerciseName="Understanding"
          setType="textset"
        />
      </View>
    </View>
  );
}

export default AuditoryLanguageComprehension;
